/**
 * PATH: packages/api/src/g10ProgressClaims.ts
 * FILE_ID: API-G10-PROGRESS-CLAIMS-001
 * PURPOSE: Narrow adapter over generated G10 claim, assessment, and profile contracts.
 * WHY: Decimal and multipart transport normalization belongs at one API seam.
 * RELATED: BackendSchemas is authoritative; local view models contain UI-normalized values.
 */
import type { BackendSchemas } from './contracts'

export type ClaimValuationMode = 'percentage' | 'measured_quantity' | 'milestone' | 'daywork'
export type ClaimDocumentFormat = 'pdf' | 'docx' | 'xlsx'

export interface G10ApiClient {
  get<T>(url: string, config?: unknown): Promise<{ data: T }>
  post<T>(url: string, body?: unknown, config?: unknown): Promise<{ data: T }>
  patch<T>(url: string, body?: unknown, config?: unknown): Promise<{ data: T }>
}

export interface ClaimSetupRequest {
  job_id: number
  contract_id: string
  doc_type: 'claim' | 'invoice'
  period: string
  period_start: string
  period_end: string
  retention_percent: number
}

export interface ClaimLineUpdate {
  id: string
  valuation_mode: ClaimValuationMode
  this_claim_percent?: number
  this_claim_amount?: number
  this_claim_quantity?: number
  evidence_ids?: string[]
}

export interface ClaimContract {
  id: string
  job: number
  accepted_quote: string
  title: string
  work_package: string
  external_contract_ref: string
  output_profile: string
  retention_rate: string
}

export interface ClaimProfile {
  id: string
  name: string
  workbook_profile_key: 'canonical-v1' | 'compact-v1' | 'sopa-v1' | 'daywork-v1' | 'assessed-v1'
  numbering_prefix: string
  numbering_pattern: string
  blocks: string[]
  retention_display: 'summary' | 'line' | 'hidden'
  statutory_wording: string
  formats: ClaimDocumentFormat[]
  mapping: {
    sheet_name?: string
    columns?: Array<{ header: string; field: string }>
  }
  mapping_version: number
  effective_from: string
  is_active: boolean
  is_default: boolean
}

export interface ClaimAssessmentLine {
  line_id: string
  description: string
  claimed_amount: number
  certified_amount: number
  reduction_reason: string
}

export interface ClaimAssessment {
  id: string | null
  claim_id: string
  status: 'draft' | 'issued' | 'accepted' | 'disputed' | 'resolved'
  reference: string
  assessment_date: string
  due_date: string | null
  assessor_name: string
  source_file_id: number | null
  source_name: string | null
  profile_key: string
  issue_checksum: string
  issued_at: string | null
  row_version: number
  lines: ClaimAssessmentLine[]
}

export interface ExtendedFinancialPosition {
  original_contract: string | null
  approved_variations: string | null
  total_claimed: string | null
  certified_total: string | null
  retention_held: string | null
  unresolved_differences: string | null
  balance_remaining: string | null
  tracker: ContractTrackerRow[]
}

export interface ContractTrackerRow {
  contract_id: string
  quote_id: string
  quote_number: string
  name: string
  type: 'contract' | 'variation'
  quotech_value: string
  client_certified_value: string
  variance: string
  status: 'approved' | 'unapproved'
  open_closed: 'open' | 'closed'
  waiting_on: string
}

export type GeneratedClaimDetail = BackendSchemas['PaymentClaimDetail']

function results<T>(data: T[] | { results: T[] }): T[] {
  return Array.isArray(data) ? data : data.results
}

export async function listClaimContracts(client: G10ApiClient, jobId: number) {
  const response = await client.get<ClaimContract[] | { results: ClaimContract[] }>(
    `/api/claims/contracts/?job=${jobId}`,
  )
  return results(response.data)
}

export async function ensureClaimContract(
  client: G10ApiClient,
  request: {
    jobId: number
    contractId?: string | null
    quoteId?: string | null
    profileId?: string | null
    paymentTermsDays: number
    retentionPercent: number
  },
) {
  const contracts = await listClaimContracts(client, request.jobId)
  if (request.contractId) {
    const selected = contracts.find((contract) => contract.id === request.contractId)
    if (!selected) throw new Error('Selected claim contract is unavailable.')
    return selected
  }
  const existing = request.quoteId
    ? contracts.find((contract) => contract.accepted_quote === request.quoteId)
    : contracts[0]
  if (existing) return existing
  if (!request.quoteId) {
    throw new Error('Create or select an accepted quote-backed claim contract first.')
  }
  return (await client.post<ClaimContract>('/api/claims/contracts/', {
    job_id: request.jobId,
    accepted_quote_id: request.quoteId,
    profile_id: request.profileId || null,
    payment_terms_days: request.paymentTermsDays,
    retention_policy: { rate: request.retentionPercent },
  })).data
}

export async function registerExternalPrior(
  client: G10ApiClient,
  request: {
    jobId: number
    contractId: string
    period: string
    externalRef: string
    percentComplete: number
    file: File
  },
) {
  const body = new FormData()
  body.append('job_id', String(request.jobId))
  body.append('contract_id', request.contractId)
  body.append('doc_type', 'claim')
  body.append('period', request.period)
  body.append('external_ref', request.externalRef)
  body.append('percent_complete', String(request.percentComplete))
  body.append('file', request.file)
  return (await client.post<GeneratedClaimDetail>(
    '/api/claims/external/register/',
    body,
  )).data
}

export async function generateConfiguredClaim(client: G10ApiClient, request: ClaimSetupRequest) {
  return (await client.post<{ id: string }>('/api/claims/generate/', request)).data
}

export async function saveClaimLineUpdates(
  client: G10ApiClient,
  claimId: string,
  rowVersion: number,
  updates: ClaimLineUpdate[],
) {
  const lineUpdates = updates.map((update) => {
    const common = { id: update.id }
    if (update.valuation_mode === 'percentage') {
      return { ...common, this_claim_percent: update.this_claim_percent ?? 0 }
    }
    if (update.valuation_mode === 'measured_quantity' || update.valuation_mode === 'daywork') {
      return { ...common, this_claim_quantity: update.this_claim_quantity ?? 0 }
    }
    return { ...common, this_claim_amount: update.this_claim_amount ?? 0 }
  })
  return (await client.post<GeneratedClaimDetail>(`/api/claims/${claimId}/update_progress/`, {
    row_version: rowVersion,
    line_updates: lineUpdates,
  })).data
}

type AssessmentApiLine = {
  contract_line: string
  claimed_amount: string
  certified_amount: string
  reduction_reason: string
}

type AssessmentApi = Omit<ClaimAssessment, 'claim_id' | 'lines' | 'source_name' | 'source_file_id'> & {
  claim: string
  source_file: number | null
  lines: AssessmentApiLine[]
}

function normalizeAssessment(data: AssessmentApi): ClaimAssessment {
  return {
    ...data,
    claim_id: data.claim,
    source_file_id: data.source_file,
    source_name: null,
    lines: data.lines.map((line) => ({
      line_id: line.contract_line,
      description: line.contract_line,
      claimed_amount: Number(line.claimed_amount),
      certified_amount: Number(line.certified_amount),
      reduction_reason: line.reduction_reason,
    })),
  }
}

export async function loadClaimAssessment(
  client: G10ApiClient,
  claim: GeneratedClaimDetail,
) {
  const response = await client.get<AssessmentApi[] | { results: AssessmentApi[] }>(
    `/api/claims/assessments/?claim=${claim.id}`,
  )
  const existing = results(response.data)[0]
  if (existing) {
    const normalized = normalizeAssessment(existing)
    const descriptions = new Map((claim.lines as Array<{
      contract_line?: string | null
      description: string
    }>).map((line) => [line.contract_line, line.description]))
    return {
      ...normalized,
      lines: normalized.lines.map((line) => ({
        ...line,
        description: descriptions.get(line.line_id) ?? line.description,
      })),
    }
  }
  const lines = (claim.lines as Array<{
    contract_line?: string | null
    description: string
    this_claim_amount: string
  }>).filter((line) => line.contract_line)
  return {
    id: null,
    claim_id: claim.id,
    status: 'draft' as const,
    reference: `Assessment ${claim.claim_number}`,
    assessment_date: new Date().toISOString().slice(0, 10),
    due_date: null,
    assessor_name: '',
    source_file_id: null,
    source_name: null,
    profile_key: 'canonical-v1',
    issue_checksum: '',
    issued_at: null,
    row_version: 1,
    lines: lines.map((line) => ({
      line_id: line.contract_line as string,
      description: line.description,
      claimed_amount: Number(line.this_claim_amount),
      certified_amount: Number(line.this_claim_amount),
      reduction_reason: '',
    })),
  }
}

export async function saveClaimAssessment(client: G10ApiClient, assessment: ClaimAssessment) {
  const payload = {
    row_version: assessment.row_version,
    claim_id: assessment.claim_id,
    reference: assessment.reference,
    assessment_date: assessment.assessment_date,
    due_date: assessment.due_date,
    assessor_name: assessment.assessor_name,
    source_file_id: assessment.source_file_id,
    profile_key: assessment.profile_key,
    lines: assessment.lines.map(({ line_id, certified_amount, reduction_reason }) => ({
      contract_line_id: line_id,
      certified_amount,
      reduction_reason,
    })),
  }
  const response = assessment.id
    ? await client.patch<AssessmentApi>(`/api/claims/assessments/${assessment.id}/`, payload)
    : await client.post<AssessmentApi>('/api/claims/assessments/', payload)
  return normalizeAssessment(response.data)
}

export async function transitionClaimAssessment(
  client: G10ApiClient,
  assessmentId: string,
  action: 'issue' | 'accept' | 'dispute' | 'resolve',
  rowVersion: number,
) {
  const response = await client.post<AssessmentApi>(
    `/api/claims/assessments/${assessmentId}/${action}/`,
    {
    row_version: rowVersion,
    },
  )
  return normalizeAssessment(response.data)
}

export async function importClaimAssessmentWorkbook(
  client: G10ApiClient,
  claimId: string,
  profile: 'assessed-v1' | 'sopa-v1',
  file: File,
) {
  const body = new FormData()
  body.append('claim_id', claimId)
  body.append('profile', profile)
  body.append('file', file)
  return (await client.post<{
    profile: string
    file_record_id: number
    file_name: string
    lines: Array<{
      contract_line_id: string
      certified_amount: string
      reduction_reason: string
    }>
  }>('/api/claims/assessments/import-xlsx/', body)).data
}

export async function listClaimProfiles(client: G10ApiClient) {
  const data = (await client.get<ClaimProfile[] | { results: ClaimProfile[] }>('/api/claims/profiles/')).data
  return results(data).map((profile) => ({
    ...profile,
    mapping: profile.mapping ?? {},
  }))
}

export async function saveClaimProfile(client: G10ApiClient, profile: ClaimProfile) {
  const url = profile.id ? `/api/claims/profiles/${profile.id}/` : '/api/claims/profiles/'
  const saved = (profile.id
    ? await client.patch<ClaimProfile>(url, profile)
    : await client.post<ClaimProfile>(url, profile)).data
  return { ...saved, mapping: saved.mapping ?? {} }
}

export function canSendClaimToXero(claim: Pick<GeneratedClaimDetail, 'available_actions'>): boolean {
  return claim.available_actions.includes('send_to_xero')
}
